import { useTheme } from '@pixee/design-system'
import { useEffect, useId, useState } from 'react'
import { match } from 'ts-pattern'
import * as styles from './mermaid-diagram.css.ts'
import { Spinner } from './spinner.tsx'

export type MermaidDiagramSizing = 'fit' | 'intrinsic'

export type MermaidDiagramProps = {
  code: string
  /**
   * `fit` (default) keeps mermaid's own `width="100%"` plus `max-width`, which scales the diagram
   * down to its container. A large graph therefore shrinks rather than scrolls — a 4000px-wide
   * trust zone flow collapses to ~24% and its edge labels become illegible.
   *
   * `intrinsic` restores the diagram's natural size from its viewBox so a caller can supply its own
   * bounded viewport with zoom and pan.
   */
  sizing?: MermaidDiagramSizing
  /**
   * Mermaid `themeVariables` overrides. Values must be concrete colours — mermaid writes them into
   * SVG presentation attributes, where a `var(--token)` reference would not resolve. Callers should
   * resolve design tokens against a mounted element before passing them in. Pass a stable/memoized
   * reference: this feeds the render effect's dependencies, so a fresh object on every parent render
   * re-runs the full mermaid parse/render cycle.
   */
  themeVariables?: Record<string, string>
  /**
   * Rounds node corners to this radius in px. Mermaid draws nodes as SVG rects, which ignore
   * `border-radius` and expose no radius themeVariable.
   */
  nodeCornerRadius?: number
  /** Rounds subgraph ("cluster") corners to this radius in px, for the same reason. */
  clusterCornerRadius?: number
  /**
   * Flowchart layout overrides — node label padding and subgraph title spacing. Pass a stable/memoized
   * reference (see `themeVariables`): a fresh object each render re-runs the mermaid render cycle.
   */
  flowchart?: { padding?: number; subGraphTitleMargin?: { top?: number; bottom?: number } }
  /**
   * Enlarges subgraph ("cluster") titles by this factor, relative to the graph's base text. Opt-in, since
   * the ordinary markdown diagrams this component also renders do not want it.
   */
  clusterLabelScale?: number
  /**
   * Fires with the rendered markup once it is in the DOM, so a wrapper can measure or re-use it. Pass a
   * stable/memoized callback: a fresh function each render re-fires this with the already-rendered svg.
   */
  onRendered?: (svgMarkup: string) => unknown
}

type MermaidRenderState =
  | { state: 'rendering' }
  | { state: 'rendered'; svg: string }
  | { state: 'error'; error: unknown }

/**
 * Restores the diagram's real pixel size from its viewBox, replacing mermaid's `width="100%"` plus
 * inline `max-width`.
 */
const applyIntrinsicSizing = (svg: Element): void => {
  const viewBox = svg.getAttribute('viewBox')?.split(/\s+/).map(Number)
  if (viewBox?.length !== 4) {
    return
  }
  const [, , intrinsicWidth, intrinsicHeight] = viewBox
  if (!Number.isFinite(intrinsicWidth) || !Number.isFinite(intrinsicHeight)) {
    return
  }
  svg.setAttribute('width', String(intrinsicWidth))
  svg.setAttribute('height', String(intrinsicHeight))
  // Strip mermaid's own max-width rather than appending after it — a trailing duplicate declaration
  // does not reliably win once the markup is re-parsed.
  const remainingStyle = (svg.getAttribute('style') ?? '').replace(/max-width\s*:[^;]*;?/gi, '').trim()
  svg.setAttribute('style', [remainingStyle.replace(/;$/, ''), 'max-width:none'].filter(Boolean).join(';'))
}

/**
 * Rounds node corners via the SVG `rx`/`ry` geometry attributes.
 *
 * `border-radius` does not apply to an SVG rect, and mermaid exposes no radius themeVariable. The CSS
 * geometry properties do work in the browser, but csstype does not model them so vanilla-extract
 * rejects them at compile time — setting the attributes here avoids that gap entirely.
 *
 * Matched with an attribute selector rather than `.node`, since class selectors are unreliable against
 * a document parsed as XML.
 */
const applyCornerRadius = ({ svg, className, radius }: { svg: Element; className: string; radius: number }): void => {
  svg.querySelectorAll(`[class~="${className}"] rect`).forEach(rect => {
    rect.setAttribute('rx', String(radius))
    rect.setAttribute('ry', String(radius))
  })
}

const parseTranslate = (element: Element): { x: number; y: number } | undefined => {
  const translateMatch = /translate\(\s*([-\d.]+)[\s,]+([-\d.]+)/.exec(element.getAttribute('transform') ?? '')
  if (!translateMatch) {
    return undefined
  }
  const [x, y] = [Number(translateMatch[1]), Number(translateMatch[2])]
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : undefined
}

const readNumericAttribute = (element: Element, name: string): number | undefined => {
  const value = Number(element.getAttribute(name))
  return Number.isFinite(value) ? value : undefined
}

/**
 * Enlarges each subgraph title, keeping it centred on its band.
 *
 * The title group is scaled rather than restyled: mermaid measures the title at the graph's base font and
 * sizes the label box from that measurement, so enlarging only the text would overflow a box that no
 * longer fits it. Scaling the group takes the box along with the text — but it grows rightward from the
 * anchor, so the anchor moves left by half the width gained to keep the title centred.
 *
 * Callers must reserve the extra vertical space via `flowchart.subGraphTitleMargin.bottom`: mermaid lays
 * each band out around a title measured at the base font, well before any of this runs.
 */
const scaleClusterLabels = ({ svg, scale }: { svg: Element; scale: number }): void => {
  svg.querySelectorAll('[class~="cluster"]').forEach(cluster => {
    const rect = cluster.querySelector('rect')
    const label = cluster.querySelector('[class~="cluster-label"]')
    const labelBox = label?.querySelector('foreignObject')
    const labelPosition = label ? parseTranslate(label) : undefined
    if (!rect || !label || !labelBox || !labelPosition) {
      return
    }
    const bandLeft = readNumericAttribute(rect, 'x')
    const bandWidth = readNumericAttribute(rect, 'width')
    const labelWidth = readNumericAttribute(labelBox, 'width')
    if (bandLeft === undefined || bandWidth === undefined || labelWidth === undefined) {
      return
    }
    const centredX = bandLeft + bandWidth / 2 - (labelWidth * scale) / 2
    label.setAttribute('transform', `translate(${centredX}, ${labelPosition.y}) scale(${scale})`)
  })
}

/**
 * Applies presentation fixes mermaid cannot express itself, in a single parse of its output.
 *
 * These have to happen on the markup rather than on the mounted node: the SVG is injected via
 * `dangerouslySetInnerHTML`, and anything re-injecting the same string (a parent re-render driven by
 * polling, for instance) would silently undo attribute edits made after mount.
 */
const transformSvgMarkup = ({
  svgMarkup,
  sizing,
  nodeCornerRadius,
  clusterCornerRadius,
  clusterLabelScale,
}: {
  svgMarkup: string
  sizing: MermaidDiagramSizing
  nodeCornerRadius: number | undefined
  clusterCornerRadius: number | undefined
  clusterLabelScale: number | undefined
}): string => {
  if (
    sizing !== 'intrinsic' &&
    nodeCornerRadius === undefined &&
    clusterCornerRadius === undefined &&
    clusterLabelScale === undefined
  ) {
    return svgMarkup
  }
  const parsed = new DOMParser().parseFromString(svgMarkup, 'image/svg+xml')
  const svg = parsed.documentElement
  if (svg.tagName !== 'svg' || svg.getElementsByTagName('parsererror').length > 0) {
    return svgMarkup
  }
  if (sizing === 'intrinsic') {
    applyIntrinsicSizing(svg)
  }
  if (nodeCornerRadius !== undefined) {
    applyCornerRadius({ svg, className: 'node', radius: nodeCornerRadius })
  }
  if (clusterCornerRadius !== undefined) {
    applyCornerRadius({ svg, className: 'cluster', radius: clusterCornerRadius })
  }
  if (clusterLabelScale !== undefined) {
    scaleClusterLabels({ svg, scale: clusterLabelScale })
  }
  return new XMLSerializer().serializeToString(svg)
}

/**
 * Renders mermaid source (e.g. from a ```mermaid markdown fence) as an SVG diagram.
 *
 * The mermaid library is imported lazily so it stays out of the main bundle, and
 * rendering is async; a spinner shows while the diagram renders. Invalid mermaid
 * source throws during render so a surrounding ErrorBoundary can handle it (which
 * also reports the failure to Sentry).
 */
export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({
  code,
  sizing = 'fit',
  themeVariables,
  nodeCornerRadius,
  clusterCornerRadius,
  flowchart,
  clusterLabelScale,
  onRendered,
}) => {
  const { theme } = useTheme()
  const elementId = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const [renderState, setRenderState] = useState<MermaidRenderState>({ state: 'rendering' })

  useEffect(() => {
    let isCancelled = false

    const renderDiagram = async () => {
      try {
        const { default: mermaid } = await import('mermaid')
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: theme === 'dark' ? 'dark' : 'neutral',
          themeVariables,
          flowchart,
        })
        await mermaid.parse(code)
        const { svg } = await mermaid.render(`mermaid-diagram-${elementId}`, code)
        if (!isCancelled) {
          setRenderState({
            state: 'rendered',
            svg: transformSvgMarkup({
              svgMarkup: svg,
              sizing,
              nodeCornerRadius,
              clusterCornerRadius,
              clusterLabelScale,
            }),
          })
        }
      } catch (error) {
        if (!isCancelled) {
          setRenderState({ state: 'error', error })
        }
      }
    }

    setRenderState({ state: 'rendering' })
    void renderDiagram()

    return () => {
      isCancelled = true
    }
  }, [
    code,
    theme,
    elementId,
    sizing,
    themeVariables,
    nodeCornerRadius,
    clusterCornerRadius,
    flowchart,
    clusterLabelScale,
  ])

  const renderedSvg = renderState.state === 'rendered' ? renderState.svg : undefined
  useEffect(() => {
    if (renderedSvg) {
      onRendered?.(renderedSvg)
    }
  }, [renderedSvg, onRendered])

  return match(renderState)
    .with({ state: 'rendering' }, () => (
      <div className={styles.pending}>
        <Spinner label="Rendering diagram" />
      </div>
    ))
    .with({ state: 'rendered' }, ({ svg }) => (
      <div
        className={sizing === 'intrinsic' ? styles.intrinsicDiagram : styles.diagram}
        role="img"
        aria-label="Diagram"
        // Safe: the markup comes from mermaid.render with securityLevel 'strict', which sanitizes the source.
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    ))
    .with({ state: 'error' }, ({ error }) => {
      throw error
    })
    .exhaustive()
}
